import axios from 'axios';

// Sessione scaduta/assente → si torna al login (tranne quando ci si è già,
// o per le chiamate auth stesse: il loro 401 è gestito dal form)
axios.interceptors.response.use(
  (r) => r,
  (error) => {
    const status = error?.response?.status;
    const url: string = error?.config?.url ?? '';
    if (status === 401 && !url.includes('/api/auth/') && window.location.pathname !== '/login') {
      window.location.href = '/login';
    }
    return Promise.reject(error);
  },
);

export async function login(email: string, password: string): Promise<{ email: string; displayName: string }> {
  const { data } = await axios.post('/api/auth/login', { email, password });
  return data;
}

/** Crea un account e apre la sessione (il cookie lo imposta il server). */
export async function register(email: string, password: string, displayName?: string): Promise<void> {
  await axios.post('/api/auth/register', { email, password, displayName });
}

export interface Me {
  email: string;
  displayName: string;
  role: string;
  /** funzioni riservate visibili a questo account (es. "invest") */
  funzioni: string[];
}

/** Chi è collegato e cosa può vedere. Il vero cancello è sul server: questo
 *  serve solo a non mostrare porte che poi si aprirebbero su un errore. */
export async function fetchMe(): Promise<Me> {
  const { data } = await axios.get<Me>('/api/auth/me');
  return data;
}

export async function logout(): Promise<void> {
  await axios.post('/api/auth/logout');
}

/** Ad come persistito dal backend (vedi entity Ad). */
export interface Ad {
  id: string;
  metaAdArchiveId: string;
  pageId: string | null;
  pageName: string | null;
  /** Varianti creative unite dal backend con "\n---\n" */
  adCreativeBodies: string | null;
  adCreativeLinkTitles: string | null;
  adCreativeLinkCaptions: string | null;
  adCreativeLinkDescriptions: string | null;
  adSnapshotUrl: string | null;
  adDeliveryStartTime: string | null;
  adDeliveryStopTime: string | null;
  /** CSV: "facebook,instagram" */
  publisherPlatforms: string | null;
  languages: string | null;
  currency: string | null;
  /** Categoria di settore assegnata all'ingest (tassonomia Adlens) */
  category: string | null;
  /** Trasparenza EU */
  euTotalReach: number | null;
  targetAges: string | null;
  targetGender: string | null;
  /** CSV nomi località; escluse con prefisso "-" */
  targetLocations: string | null;
  /** Derivati calcolati all'ingest */
  targetAgeMin: number | null;
  targetAgeMax: number | null;
  variantsCount: number | null;
  deliveryDays: number | null;
  firstSeenAt: string;
  lastSeenAt: string;
}

/** Page<T> di Spring Data, ridotto ai campi che usiamo. */
export interface PageResponse<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  number: number;
  last: boolean;
}

export interface SearchOutcome {
  total: number;
  inserted: number;
  updated: number;
  nextCursor: string | null;
  pages?: PageMatch[];
}

export type LocalSort = 'recent' | 'reach' | 'startTime' | 'days';

export interface LocalSearchParams {
  q?: string;
  pageId?: string;
  category?: string;
  platform?: string;
  active?: boolean;
  startFrom?: string; // yyyy-MM-dd
  startTo?: string;
  /** Località target (es. "Trieste"): match sul targeting dichiarato */
  location?: string;
  /** Paese con reach effettivo (ISO2, es. "IT") */
  country?: string;
  language?: string;
  gender?: 'Women' | 'Men' | 'All';
  /** "targetizza chi ha N anni" */
  age?: number;
  minReach?: number;
  /** giorni minimi di delivery (longevità = proxy di performance) */
  minDays?: number;
  /** ≥2 = annunci in A/B test */
  minVariants?: number;
  /** entrati in archivio negli ultimi N giorni */
  newDays?: number;
  sort?: LocalSort;
  page?: number;
  size?: number;
}

export async function searchLocal(params: LocalSearchParams): Promise<PageResponse<Ad>> {
  const { data } = await axios.get<PageResponse<Ad>>('/api/search/local', {
    params: {
      q: params.q || undefined,
      pageId: params.pageId || undefined,
      category: params.category || undefined,
      platform: params.platform || undefined,
      active: params.active,
      startFrom: params.startFrom || undefined,
      startTo: params.startTo || undefined,
      location: params.location || undefined,
      country: params.country || undefined,
      language: params.language || undefined,
      gender: params.gender || undefined,
      age: params.age,
      minReach: params.minReach,
      minDays: params.minDays,
      minVariants: params.minVariants,
      newDays: params.newDays,
      sort: params.sort === 'recent' ? undefined : params.sort,
      page: params.page ?? 0,
      size: params.size ?? 20,
    },
  });
  return data;
}

/** Risultato locale aggregato per pagina, prima del drill-down sulle creative. */
export interface PageMatch {
  pageId: string;
  pageName: string | null;
  adsCount: number;
  activeCount: number;
  totalReach: number;
  avgDays: number | null;
  lastSeenAt: string | null;
}

export async function searchLocalPages(params: LocalSearchParams): Promise<PageMatch[]> {
  const { data } = await axios.get<PageMatch[]>('/api/search/local/pages', {
    params: {
      q: params.q || undefined,
      category: params.category || undefined,
      platform: params.platform || undefined,
      active: params.active,
      startFrom: params.startFrom || undefined,
      startTo: params.startTo || undefined,
      location: params.location || undefined,
      country: params.country || undefined,
      language: params.language || undefined,
      gender: params.gender || undefined,
      age: params.age,
      minReach: params.minReach,
      minDays: params.minDays,
      minVariants: params.minVariants,
      newDays: params.newDays,
      limit: 100,
    },
  });
  return data;
}

export interface LiveSearchParams {
  searchTerms: string;
  /** Flusso che ha avviato la ricerca, mostrato nella pagina Attività. */
  origin?: 'ricerca' | 'analisi' | 'feed';
  /** KEYWORD_UNORDERED (ampia, default) | KEYWORD_EXACT_PHRASE (frase esatta) */
  searchType?: string;
  countries: string[];
  /** Categoria di settore da stampare sugli ad ingeriti */
  category?: string;
  /** Categoria speciale Meta (casa, lavoro, credito, politica) */
  adType?: string;
  adActiveStatus?: 'ALL' | 'ACTIVE' | 'INACTIVE';
  mediaType?: 'ALL' | 'IMAGE' | 'VIDEO' | 'MEME' | 'NONE';
  platforms?: string[];
  dateFrom?: string; // yyyy-MM-dd, su ad_delivery_date_min
  dateTo?: string;
  limit?: number;
}

export async function searchLive(params: LiveSearchParams): Promise<SearchOutcome> {
  const { data } = await axios.post<SearchOutcome>('/api/search/live', {
    searchTerms: params.searchTerms,
    origin: params.origin,
    searchType: params.searchType || undefined,
    countries: params.countries,
    category: params.category || undefined,
    adType: params.adType ?? 'ALL',
    adActiveStatus: params.adActiveStatus ?? 'ALL',
    mediaType: params.mediaType && params.mediaType !== 'ALL' ? params.mediaType : undefined,
    platforms: params.platforms?.length ? params.platforms : undefined,
    dateFrom: params.dateFrom || undefined,
    dateTo: params.dateTo || undefined,
    limit: params.limit ?? 50,
  });
  return data;
}

/** Ricerca live registrata dal backend dopo un'acquisizione completata. */
export interface SearchHistory {
  id: string;
  searchTerms: string;
  /** CSV dei mercati interrogati, es. "IT,DE". */
  countries: string | null;
  origin: 'ricerca' | 'analisi' | 'feed' | string;
  total: number;
  inserted: number;
  updated: number;
  executedAt: string;
}

export async function fetchSearchHistory(page = 0, size = 30): Promise<PageResponse<SearchHistory>> {
  const { data } = await axios.get<PageResponse<SearchHistory>>('/api/search/history', {
    params: { page, size },
  });
  return data;
}

export async function deleteSearchHistory(id: string): Promise<void> {
  await axios.delete(`/api/search/history/${encodeURIComponent(id)}`);
}

export type SavedAnalysisMode = 'local' | 'online';

export interface SavedAnalysisListItem {
  id: string;
  title: string;
  mode: SavedAnalysisMode;
  description: string;
  summary: string | null;
  createdAt: string;
}

export interface SavedAnalysis<TPayload = unknown> extends SavedAnalysisListItem {
  payload: TPayload;
}

export interface SaveAnalysisRequest<TPayload> {
  title?: string;
  mode: SavedAnalysisMode;
  description: string;
  summary?: string;
  payload: TPayload;
}

export async function createSavedAnalysis<TPayload>(
  request: SaveAnalysisRequest<TPayload>,
): Promise<SavedAnalysis<TPayload>> {
  const { data } = await axios.post<SavedAnalysis<TPayload>>('/api/analyses', request);
  return data;
}

export async function fetchSavedAnalyses(page = 0, size = 20): Promise<PageResponse<SavedAnalysisListItem>> {
  const { data } = await axios.get<PageResponse<SavedAnalysisListItem>>('/api/analyses', {
    params: { page, size },
  });
  return data;
}

export async function fetchSavedAnalysis<TPayload>(id: string): Promise<SavedAnalysis<TPayload>> {
  const { data } = await axios.get<SavedAnalysis<TPayload>>(`/api/analyses/${encodeURIComponent(id)}`);
  return data;
}

export async function deleteSavedAnalysis(id: string): Promise<void> {
  await axios.delete(`/api/analyses/${encodeURIComponent(id)}`);
}

export interface CategoryCount {
  category: string;
  count: number;
}

export interface PageSuggestion {
  pageId: string;
  pageName: string;
  adsCount: number;
}

/** Typeahead: pagine in archivio che matchano il testo digitato. */
export async function fetchSuggestions(q: string): Promise<PageSuggestion[]> {
  if (q.trim().length < 2) return [];
  const { data } = await axios.get<PageSuggestion[]>('/api/search/suggest', {
    params: { q: q.trim() },
  });
  return data;
}

/** Categorie di settore presenti in archivio, con conteggio. */
export async function fetchCategories(): Promise<CategoryCount[]> {
  const { data } = await axios.get<CategoryCount[]>('/api/search/categories');
  return data;
}

/** Vista "Pagine": aggregato ads per pagina + profilo business Google. */
export interface PageView {
  pageId: string;
  pageName: string | null;
  category: string | null;
  adsCount: number;
  activeCount: number;
  totalReach: number;
  avgDays: number | null;
  lastSeenAt: string | null;
  profileStatus: 'ENRICHED' | 'NOT_FOUND' | 'ERROR' | null;
  rating: number | null;
  reviewsCount: number | null;
  address: string | null;
  phone: string | null;
  website: string | null;
  mapsUrl: string | null;
  /** Performance mobile 0-100 (Google PageSpeed), null se mai misurata */
  pagespeedScore: number | null;
  /** Email trovate (pagina FB + sito), CSV */
  emails: string | null;
  /** Analisi AI dei copy (null se mai generata) */
  aiAnalysis: string | null;
  /** Quota % del reach della sua categoria (share of voice) */
  shareOfCategory: number | null;
}

/** Annuncio con i segnali pubblici più forti all'interno della stessa pagina. */
export interface WinnerAd {
  adArchiveId: string;
  snapshotBody: string | null;
  reach: number | null;
  deliveryDays: number | null;
  variantsCount: number | null;
  active: boolean;
  score: number;
  reasons: string[];
}

export async function fetchWinners(pageId: string, limit = 5): Promise<WinnerAd[]> {
  const { data } = await axios.get<WinnerAd[]>(
    `/api/pages/${encodeURIComponent(pageId)}/winners`,
    { params: { limit } },
  );
  return data;
}

/** Reach reale per fascia d'età/genere (breakdown Meta aggregato). */
export interface DemographicBucket {
  ageRange: string;
  male: number;
  female: number;
  unknown: number;
}

export async function fetchDemographics(pageId: string): Promise<DemographicBucket[]> {
  const { data } = await axios.get<DemographicBucket[]>(
    `/api/pages/${encodeURIComponent(pageId)}/demographics`,
  );
  return data;
}

export async function measurePageSpeed(pageId: string): Promise<PageView> {
  const { data } = await axios.post<PageView>(
    `/api/pages/${encodeURIComponent(pageId)}/pagespeed`,
  );
  return data;
}

export async function fetchPages(): Promise<PageView[]> {
  const { data } = await axios.get<PageView[]>('/api/pages');
  return data;
}

/** Attività locali trovate su Google Places per servizio e zona. */
export interface MarketCompetitor {
  name: string | null;
  rating: number | null;
  reviewsCount: number | null;
  address: string | null;
  website: string | null;
  mapsUrl: string | null;
  /** Euristica backend: sito presente e almeno 50 recensioni. */
  strongPresence: boolean;
  /** Distanza dal centro scelto, disponibile quando la ricerca usa un raggio. */
  distanceMeters: number | null;
  /** Tipo principale restituito da Google Places, es. "Pizzeria". */
  primaryType: string | null;
}

export interface MarketCenter {
  lat: number;
  lng: number;
}

export interface MarketGaps {
  reading: string | null;
  missing: string[];
  oversupplied: string[];
}

export interface MarketMap {
  query: string;
  area: string | null;
  count: number;
  avgRating: number;
  medianReviews: number;
  leader: string | null;
  competitors: MarketCompetitor[];
  center: MarketCenter | null;
  radiusMeters: number | null;
  /** Censimento ISTAT 2021 per raggi >=1,5 km; stima WorldPop 2020 per raggi più piccoli. */
  population: number | null;
  gaps: MarketGaps | null;
}

export interface MarketNearbyOptions {
  address?: string;
  radiusMeters?: number;
}

export type OpportunityKind = 'gap' | 'quality' | 'fragmentation';

export interface OpportunityCensusRow {
  key: string;
  label: string;
  count: number;
  capped: boolean;
  avgRating: number | null;
  totalReviews: number;
  densityPer10k: number | null;
  benchmarkPer10k: number;
}

export interface ZoneOpportunity {
  key: string;
  label: string;
  kind: OpportunityKind;
  score: number;
  evidence: string[];
}

export type ImportableTrendKind = 'diffuso' | 'frontiera';
export type ImportableTrendDomain = 'food' | 'tempo libero' | 'servizi' | 'retail';

export interface ImportableTrend {
  key: string;
  label: string;
  kind: ImportableTrendKind;
  /** Optional so results saved before section 24 remain restorable. */
  domain?: ImportableTrendDomain;
  localCount: number;
  hubAvg: number;
  topHub: string;
  milanCount: number | null;
  score: number;
  evidence: string[];
}

export interface CreativeOpportunityIdea {
  idea: string;
  rationale: string;
  basedOn: string[];
}

export interface MarketRentQuote {
  zoneName: string | null;
  fascia: string | null;
  minSqm: number;
  medSqm: number;
  maxSqm: number;
  year: number;
  semester: number;
  monthlyFor60sqm: number;
}

export interface OpportunityRegistryCategory {
  key: string;
  label: string;
  count: number;
}

export interface OpportunityRegistrySummary {
  totalCompanies: number;
  newLast90d: number;
  byCategory: OpportunityRegistryCategory[];
}

export interface MarketOpportunityResult {
  center: MarketCenter;
  radiusMeters: number;
  population: number | null;
  businessesFound: number;
  census: OpportunityCensusRow[];
  opportunities: ZoneOpportunity[];
  /** Optional so analyses saved before block 23 remain restorable. */
  importableTrends?: ImportableTrend[];
  /** Optional so analyses saved before section 24 remain restorable. */
  creativeIdeas?: CreativeOpportunityIdea[];
  /** Optional so analyses saved before section 27 remain restorable. */
  rentQuote?: MarketRentQuote | null;
  /** Optional so analyses saved before section 29 remain restorable. */
  registry?: OpportunityRegistrySummary | null;
  reading: string | null;
  disclaimer: string;
}

export async function fetchMarketOpportunities(
  address: string,
  radiusMeters = 2_000,
): Promise<MarketOpportunityResult> {
  const { data } = await axios.post<MarketOpportunityResult>('/api/market/opportunities', {
    address: address.trim(),
    radiusMeters,
  }, { timeout: 150_000 });
  return data;
}

export interface AddressSuggestion {
  label: string;
  main: string | null;
  secondary: string | null;
}

export async function suggestMarketAddresses(
  query: string,
  limit = 6,
  signal?: AbortSignal,
): Promise<AddressSuggestion[]> {
  const normalizedQuery = query.trim();
  if (normalizedQuery.length < 3) return [];
  const { data } = await axios.get<AddressSuggestion[]>('/api/market/suggest-address', {
    params: { q: normalizedQuery, limit },
    signal,
  });
  return data;
}

export async function fetchMarketNearby(
  query: string,
  area: string,
  maxResults = 15,
  options: MarketNearbyOptions = {},
): Promise<MarketMap> {
  const { data } = await axios.post<MarketMap>('/api/market/nearby', {
    query: query.trim(),
    area: area.trim() || undefined,
    maxResults,
    address: options.address?.trim() || undefined,
    radiusMeters: options.radiusMeters,
  }, { timeout: 90_000 });
  return data;
}

/** Keyword proposte dall'AI per trovare competitor nell'Ad Library. */
export interface DiscoverKeyword {
  term: string;
  rationale: string;
}

export interface DiscoverKeywordGroup {
  language: string;
  countries: string[];
  keywords: DiscoverKeyword[];
}

export interface HashtagSignal {
  tag: string;
  count: number;
  sources: string[];
}

export interface HashtagResult {
  hashtags: HashtagSignal[];
  tiktokUsed: boolean;
  tiktokError: string | null;
}

export type CompetitorLabel = 'diretto' | 'adiacente' | 'irrilevante';

export interface CompetitorClassification {
  pageId: string;
  pageName: string | null;
  label: CompetitorLabel;
  reason: string;
  adsCount: number;
  totalReach: number;
  countries: string[];
}

export interface SaasCompetitor {
  name: string;
  vendor: string | null;
  slug: string;
  description: string | null;
  category: string | null;
  rating: number | null;
  reviews: number | null;
  entryPrice: string | null;
  url: string | null;
  logoUrl: string | null;
  position: number | null;
  sponsored: boolean;
  awards: string[];
  scrapedAt: string | null;
  label: CompetitorLabel;
  reason: string;
  source: 'g2';
}

export interface SaasDiscoveryResult {
  categories: string[];
  candidates: number;
  competitors: SaasCompetitor[];
  error: string | null;
}

export interface DiscoverySeeds {
  siteUrl?: string;
  knownCompetitors?: string[];
}

export async function discoverKeywords(
  description: string,
  countries: string[] = ['IT'],
  seeds: DiscoverySeeds = {},
): Promise<DiscoverKeywordGroup[]> {
  const { data } = await axios.post<DiscoverKeywordGroup[]>('/api/discover/keywords', {
    description: description.trim(),
    countries,
    siteUrl: seeds.siteUrl?.trim() || undefined,
    knownCompetitors: seeds.knownCompetitors?.length ? seeds.knownCompetitors : undefined,
  });
  return data;
}

export async function discoverHashtags(terms: string[], includeTikTok = true): Promise<HashtagResult> {
  const { data } = await axios.post<HashtagResult>('/api/discover/hashtags', {
    terms,
    includeTikTok,
  }, { timeout: 240_000 });
  return data;
}

export async function discoverClassify(
  description: string,
  pageIds: string[],
  knownCompetitors: string[] = [],
): Promise<CompetitorClassification[]> {
  const { data } = await axios.post<CompetitorClassification[]>('/api/discover/classify', {
    description: description.trim(),
    pageIds,
    knownCompetitors: knownCompetitors.length ? knownCompetitors : undefined,
  });
  return data;
}

export async function discoverSaas(
  description: string,
  knownCompetitors: string[] = [],
): Promise<SaasDiscoveryResult> {
  const { data } = await axios.post<SaasDiscoveryResult>('/api/discover/saas', {
    description: description.trim(),
    knownCompetitors: knownCompetitors.length ? knownCompetitors : undefined,
  }, { timeout: 240_000 });
  return data;
}

export async function enrichPage(pageId: string, force = false): Promise<PageView> {
  const { data } = await axios.post<PageView>(
    `/api/pages/${encodeURIComponent(pageId)}/enrich`,
    null,
    { params: { force: force || undefined } },
  );
  return data;
}

/** Watchlist: ricerca salvata rieseguita ogni mattina dal cron. */
export interface Watchlist {
  id: string;
  name: string;
  searchParams: {
    searchTerms: string;
    countries: string[] | null;
    category: string | null;
    limit: number | null;
    pageIds?: string[] | null;
  };
  active: boolean;
  lastRunAt: string | null;
  createdAt: string;
}

export async function fetchWatchlists(): Promise<Watchlist[]> {
  const { data } = await axios.get<Watchlist[]>('/api/watchlists');
  return data;
}

export async function createWatchlist(body: {
  name: string;
  searchTerms: string;
  countries: string[];
  category?: string;
  limit?: number;
  pageIds?: string[];
}): Promise<Watchlist> {
  const { data } = await axios.post<Watchlist>('/api/watchlists', body);
  return data;
}

export async function runWatchlist(id: string): Promise<SearchOutcome> {
  const { data } = await axios.post<SearchOutcome>(
    `/api/watchlists/${encodeURIComponent(id)}/run`,
  );
  return data;
}

export async function setWatchlistActive(id: string, value: boolean): Promise<Watchlist> {
  const { data } = await axios.patch<Watchlist>(
    `/api/watchlists/${encodeURIComponent(id)}/active`,
    null,
    { params: { value } },
  );
  return data;
}

export async function deleteWatchlist(id: string): Promise<void> {
  await axios.delete(`/api/watchlists/${encodeURIComponent(id)}`);
}

/** Raccolta storica: zone e segnali di bisogno osservati nel tempo. */
export type CollectionTargetKind = 'zona' | 'demand_zone';
export type CollectionFrequency = 'daily' | 'weekly' | 'monthly' | 'quarterly';
export type CollectionRunStatus = 'running' | 'ok' | 'error' | 'skipped_budget';
export type CollectionRunKind = CollectionTargetKind | 'zona-registro';

export interface CollectionTargetConfig {
  address?: string;
  radiusMeters?: number;
  lat?: number;
  lng?: number;
  city?: string;
  sector?: string;
  patterns?: string[];
  origin?: string;
}

export interface CollectionTarget {
  id: string;
  kind: CollectionTargetKind;
  label: string;
  config: CollectionTargetConfig;
  frequency: CollectionFrequency;
  active: boolean;
  lastRunAt: string | null;
  createdAt: string;
}

export type CollectionInsightIcon = 'aperture' | 'chiusure' | 'crescita' | 'registro' | 'bisogno';
export type CollectionInsightLevel = 'alto' | 'medio' | 'basso';

export interface CollectionInsight {
  icon: CollectionInsightIcon;
  title: string;
  detail: string;
  level: CollectionInsightLevel;
  targetId: string;
  targetLabel: string;
}

export interface ZoneHistoryCensusPoint {
  capturedAt: string;
  businessesFound: number;
  population: number | null;
}

export interface ZoneBusinessChange {
  businessId: string;
  name: string | null;
  categoryKey: string | null;
  lat: number | null;
  lng: number | null;
  when: string;
}

export interface ZoneGrowthRow {
  businessId: string;
  name: string | null;
  categoryKey: string | null;
  reviewsDelta: number;
  reviewsPerMonth: number;
  lastRating: number | null;
}

export interface ZoneRegistryPoint {
  capturedAt: string;
  totalCompanies: number;
  newRegistrations: number | null;
}

export interface ZoneCategorySeries {
  categoryKey: string;
  counts: number[];
  capturedAt: string[];
}

export interface ZoneHistory {
  censusTimeline: ZoneHistoryCensusPoint[];
  newBusinesses: ZoneBusinessChange[];
  goneBusinesses: ZoneBusinessChange[];
  topGrowing: ZoneGrowthRow[];
  registrySeries: ZoneRegistryPoint[];
  categoryTrends: ZoneCategorySeries[];
}

export interface ZoneBusiness {
  id: string;
  targetId: string;
  placeId: string;
  name: string | null;
  categoryKey: string | null;
  primaryType: string | null;
  lat: number | null;
  lng: number | null;
  firstSeenAt: string;
  lastSeenAt: string;
}

/** Posizione e raggio condivisi dalle letture ampie della zona. */
export interface ZoneInsightLocation {
  address?: string;
  lat?: number;
  lng?: number;
  radiusMeters?: number;
}

export interface ZoneVehicleCount {
  comune: string;
  autovetture: number;
  anno: number;
}

/** Categoria OpenStreetMap restituita dalla ricerca estesa della zona. */
export interface ZoneOpenCategory {
  tagKey: string;
  category: string;
  etichetta: string;
  conteggio: number;
}

export interface ZoneActivities {
  lat: number;
  lng: number;
  radiusMeters: number;
  totale: number;
  categorieDiverse: number;
  abitanti: number | null;
  auto: ZoneVehicleCount | null;
  categorie: ZoneOpenCategory[];
}

export interface ZoneTrade {
  key: string;
  label: string;
  cosaCerca: string;
  categorie: string[];
  abitanti: boolean;
  auto: boolean;
}

export interface ZoneTradeCustomers {
  mestiere: string;
  label: string;
  cosaCerca: string;
  clientiPotenziali: number;
  dettaglio: ZoneOpenCategory[];
  abitanti: number | null;
  auto: ZoneVehicleCount | null;
  lettura: string;
}

function zoneInsightParams(location: ZoneInsightLocation) {
  const hasCoordinates = location.lat != null && location.lng != null;
  return {
    // L'indirizzo resta utile anche con coordinate precise: il backend lo usa
    // per associare il comune e restituire il dato ACI sulle autovetture.
    address: location.address || undefined,
    lat: hasCoordinates ? location.lat : undefined,
    lng: hasCoordinates ? location.lng : undefined,
    radiusMeters: location.radiusMeters ?? 2_000,
  };
}

export async function fetchZoneActivities(location: ZoneInsightLocation, q = ''): Promise<ZoneActivities> {
  const { data } = await axios.get<ZoneActivities>('/api/market/zone/activities', {
    params: { ...zoneInsightParams(location), q: q.trim() || undefined },
  });
  return data;
}

export async function fetchZoneTrades(): Promise<ZoneTrade[]> {
  const { data } = await axios.get<ZoneTrade[]>('/api/market/zone/trades');
  return data;
}

export async function fetchZoneCustomers(trade: string, location: ZoneInsightLocation): Promise<ZoneTradeCustomers> {
  const { data } = await axios.get<ZoneTradeCustomers>('/api/market/zone/customers', {
    params: { trade, ...zoneInsightParams(location) },
  });
  return data;
}

export interface CollectionRun {
  id: string;
  targetId: string;
  kind: CollectionRunKind;
  startedAt: string;
  finishedAt: string | null;
  status: CollectionRunStatus;
  items: number;
  costEstimateCents: number;
  error: string | null;
}

export interface CollectionBudget {
  placesBudgetCents: number;
  placesFreeCallsMonthly?: number;
  capCents?: number;
  spentCents: number;
  remainingCents: number;
  openapiBudgetCents?: number;
  openapiSpentCents?: number;
  openapiRemainingCents?: number;
}

export interface ZoneCensusHistoryRow {
  id: string;
  censusId: string;
  categoryKey: string;
  count: number;
  capped: boolean;
  avgRating: number | null;
  totalReviews: number;
}

export interface ZoneCensusHistory {
  id: string;
  capturedAt: string;
  population: number;
  businessesFound: number;
  rows: ZoneCensusHistoryRow[];
}

export interface CollectionDemandSignal {
  id: string;
  targetId: string;
  source: string;
  querySeed: string | null;
  signal: string;
  firstSeenAt: string;
  lastSeenAt: string;
  seenCount: number;
}

export interface CollectionDemandCluster {
  id: string;
  targetId: string;
  period: string;
  clusterLabel: string;
  signalsCount: number;
  examples: string[] | null;
  createdAt: string;
}

export interface OsmCategoryStat {
  tag_key: string;
  category: string;
  count: number;
}

export interface CreateCollectionTargetInput {
  kind: CollectionTargetKind;
  label: string;
  config: CollectionTargetConfig;
  frequency: CollectionFrequency;
}

export async function fetchCollectionTargets(): Promise<CollectionTarget[]> {
  const { data } = await axios.get<CollectionTarget[]>('/api/collect/targets');
  return data;
}

export async function fetchCollectionInsights(limit = 6): Promise<CollectionInsight[]> {
  const { data } = await axios.get<CollectionInsight[]>('/api/collect/insights', {
    params: { limit },
  });
  return data;
}

export async function fetchZoneHistory(id: string, windowDays = 90): Promise<ZoneHistory> {
  const { data } = await axios.get<ZoneHistory>(
    `/api/collect/targets/${encodeURIComponent(id)}/history`,
    { params: { windowDays } },
  );
  return data;
}

export async function fetchZoneBusinesses(id: string, category?: string): Promise<ZoneBusiness[]> {
  const { data } = await axios.get<ZoneBusiness[]>(
    `/api/collect/targets/${encodeURIComponent(id)}/businesses`,
    { params: { category: category || undefined } },
  );
  return data;
}

export async function createCollectionTarget(body: CreateCollectionTargetInput): Promise<CollectionTarget> {
  const { data } = await axios.post<CollectionTarget>('/api/collect/targets', body);
  return data;
}

export async function setCollectionTargetActive(id: string, active: boolean): Promise<CollectionTarget> {
  const { data } = await axios.patch<CollectionTarget>(
    `/api/collect/targets/${encodeURIComponent(id)}`,
    null,
    { params: { active } },
  );
  return data;
}

export async function deleteCollectionTarget(id: string): Promise<void> {
  await axios.delete(`/api/collect/targets/${encodeURIComponent(id)}`);
}

export async function runCollectionTarget(id: string): Promise<CollectionRun> {
  const { data } = await axios.post<CollectionRun>(`/api/collect/targets/${encodeURIComponent(id)}/run`);
  return data;
}

export async function fetchCollectionRuns(page = 0, size = 100): Promise<PageResponse<CollectionRun>> {
  const { data } = await axios.get<PageResponse<CollectionRun>>('/api/collect/runs', {
    params: { page, size },
  });
  return data;
}

export async function fetchCollectionBudget(): Promise<CollectionBudget> {
  const { data } = await axios.get<CollectionBudget>('/api/collect/budget');
  return data;
}

export async function fetchZoneCensuses(id: string): Promise<ZoneCensusHistory[]> {
  const { data } = await axios.get<ZoneCensusHistory[]>(
    `/api/collect/targets/${encodeURIComponent(id)}/censuses`,
  );
  return data;
}

export async function fetchCollectionSignals(id: string, page = 0, size = 50): Promise<PageResponse<CollectionDemandSignal>> {
  const { data } = await axios.get<PageResponse<CollectionDemandSignal>>(
    `/api/collect/targets/${encodeURIComponent(id)}/signals`,
    { params: { page, size } },
  );
  return data;
}

export async function fetchCollectionDemandClusters(id: string): Promise<CollectionDemandCluster[]> {
  const { data } = await axios.get<CollectionDemandCluster[]>(
    `/api/collect/targets/${encodeURIComponent(id)}/clusters`,
  );
  return data;
}

export async function runCollectionDemandClusters(id: string): Promise<{ clusters: number }> {
  const { data } = await axios.post<{ clusters: number }>(
    `/api/collect/targets/${encodeURIComponent(id)}/clusters/run`,
  );
  return data;
}

export async function fetchOsmStats(
  lat: number,
  lng: number,
  radiusMeters = 2_000,
): Promise<OsmCategoryStat[]> {
  const { data } = await axios.get<OsmCategoryStat[]>('/api/collect/osm/stats', {
    params: { lat, lng, radiusMeters },
  });
  return data;
}

/** Anagrafica ufficiale e ultimo bilancio depositato del competitor. */
export interface CompanyProfile {
  vat: string;
  companyName: string | null;
  legalForm: string | null;
  activityStatus: string | null;
  atecoCode: string | null;
  atecoDescription: string | null;
  startDate: string | null;
  pec: string | null;
  balanceYear: number | null;
  employees: number | null;
  turnover: number | null;
  netWorth: number | null;
  shareCapital: number | null;
  payload: unknown;
  fetchedAt: string;
}

export async function fetchCompanyProfile(pageId: string, force = false): Promise<CompanyProfile> {
  const { data } = await axios.post<CompanyProfile>(
    `/api/pages/${encodeURIComponent(pageId)}/company`,
    null,
    { params: { force: force || undefined }, timeout: 90_000 },
  );
  return data;
}

/**
 * Solo indicatori societari elaborati, ammessi nel report scaricabile.
 * I valori contabili grezzi non fanno parte di questo contratto.
 */
export interface CompanyReportIndicators {
  dimensione: string | null;
  fasciaFatturato: string | null;
  organico: string | null;
  solidita: 'verde' | 'giallo' | 'rosso' | null;
  soliditaLabel: string | null;
  tendenza: string | null;
  anniAttivita: number | null;
  annoBilancio: number | null;
  serieAnnuale: CompanyAnnualIndicator[];
  crescitaMedia: string | null;
  fonte: string;
}

export interface CompanyAnnualIndicator {
  anno: number;
  dimensione: string | null;
  variazionePct: number | null;
  organico: string | null;
  produttivita: string | null;
  incidenzaPersonale: string | null;
  solidita: 'verde' | 'giallo' | 'rosso' | null;
}

export async function fetchCompanyIndicators(vat: string): Promise<CompanyReportIndicators> {
  const { data } = await axios.get<CompanyReportIndicators>(
    `/api/company/${encodeURIComponent(vat)}/indicators`,
  );
  return data;
}

/** Profilo societario grezzo ammesso soltanto nelle viste in-app, mai nel report. */
export async function fetchCompanyByVat(vat: string): Promise<CompanyProfile> {
  const { data } = await axios.get<CompanyProfile>(
    `/api/company/${encodeURIComponent(vat)}`,
    { timeout: 90_000 },
  );
  return data;
}

/** Candidata del Registro Imprese: la UI deve sempre richiedere una conferma manuale. */
export interface CompanyCandidate {
  vat: string;
  companyName: string;
  town: string | null;
  street: string | null;
  sedeCoincide: boolean | null;
  registeredYear: number | null;
  score: number;
}

export interface FindCompanyOptions {
  name: string;
  lat?: number | null;
  lng?: number | null;
  radiusMeters?: number | null;
  address?: string | null;
}

/** Ricerca on-demand per ragione sociale nel contesto geografico dell'attivita. */
export async function findCompanies(options: FindCompanyOptions): Promise<CompanyCandidate[]> {
  const { data } = await axios.get<CompanyCandidate[]>('/api/company/find', {
    params: {
      name: options.name,
      lat: options.lat ?? undefined,
      lng: options.lng ?? undefined,
      radiusMeters: options.radiusMeters ?? undefined,
      address: options.address ?? undefined,
    },
    timeout: 90_000,
  });
  return data;
}

export interface OfficialDocumentOption {
  type: string;
  label: string;
  priceCents: number;
}

export interface OfficialDocumentOrder {
  orderId: string;
  type: string;
  label: string;
  status: string;
  ready: boolean;
  priceCents: number;
  createdAt: string;
  /** Copia salvata da Osservio e scaricabile senza dipendere dal fornitore. */
  archiviato: boolean;
  vat: string;
}

export interface CompanyDocuments {
  available: OfficialDocumentOption[];
  orders: OfficialDocumentOrder[];
}

export interface CatastoStepResult {
  requestId: string;
  kind: 'indirizzo' | 'immobili' | 'visura' | string;
  status: string;
  ready: boolean;
  result: unknown;
  priceCents: number;
}

export interface CatastoHistoryRequest {
  id: string;
  kind: 'indirizzo' | 'immobili' | 'visura' | string;
  remoteId: string;
  label: string;
  status: string;
  priceCents: number;
  payload: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CatastoSearchInput {
  provincia: string;
  comune: string;
  via: string;
}

export interface CatastoImmobiliInput {
  idIndirizzo: string;
  civico: string;
  label: string;
}

export interface CatastoVisuraInput {
  idImmobile: string;
  label: string;
}

/** Archivio completo degli acquisti, indipendente dalla scheda o dalla P.IVA. */
export async function fetchOfficialDocuments(): Promise<OfficialDocumentOrder[]> {
  const { data } = await axios.get<OfficialDocumentOrder[]>('/api/documents');
  return data;
}

/** Listino camerale e ordini già effettuati per una P.IVA sbloccata. */
export async function fetchCompanyDocuments(vat: string): Promise<CompanyDocuments> {
  const { data } = await axios.get<CompanyDocuments>(
    `/api/company/${encodeURIComponent(vat)}/documents`,
    { timeout: 90_000 },
  );
  return data;
}

/** SPESA REALE: chiamare soltanto dopo la doppia conferma esplicita in UI. */
export async function orderOfficialDocument(vat: string, type: string): Promise<OfficialDocumentOrder> {
  const { data } = await axios.post<OfficialDocumentOrder>(
    `/api/company/${encodeURIComponent(vat)}/documents/${encodeURIComponent(type)}`,
    null,
    { timeout: 90_000 },
  );
  return data;
}

export async function fetchOfficialDocumentOrder(orderId: string): Promise<OfficialDocumentOrder> {
  const { data } = await axios.get<OfficialDocumentOrder>(
    `/api/documents/${encodeURIComponent(orderId)}`,
  );
  return data;
}

/** Contesto già sbloccato nel dettaglio locale, usato per il dossier PDF server-side. */
export interface BusinessReportCompetitor {
  name: string;
  rating: number | null;
  reviewsCount: number | null;
}

export interface BusinessReportRequest {
  name: string;
  lens: 'acquisizione' | 'apertura';
  category?: string | null;
  rating?: number | null;
  reviewsCount?: number | null;
  distanceMeters?: number | null;
  address?: string | null;
  website?: string | null;
  mapsUrl?: string | null;
  competitorsInRadius?: number | null;
  radiusMeters?: number | null;
  vat?: string | null;
  metaPageId?: string | null;
  zoneTargetId?: string | null;
  competitors?: BusinessReportCompetitor[];
}

export interface GeneratedBusinessReport {
  blob: Blob;
  filename: string;
  reportId?: string;
}

function reportFilename(contentDisposition: string | undefined, name: string) {
  const encoded = contentDisposition?.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
  if (encoded) {
    try { return decodeURIComponent(encoded.replace(/^"|"$/g, '')); } catch { /* usa il formato semplice */ }
  }
  const simple = contentDisposition?.match(/filename="?([^";]+)"?/i)?.[1]?.trim();
  if (simple) return simple;
  const slug = name.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'attivita';
  return `osservio-${slug}.pdf`;
}

function attachmentFilename(contentDisposition: string | undefined, fallback: string) {
  const encoded = contentDisposition?.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
  if (encoded) {
    try { return decodeURIComponent(encoded.replace(/^"|"$/g, '')); } catch { /* usa il formato semplice */ }
  }
  return contentDisposition?.match(/filename="?([^";]+)"?/i)?.[1]?.trim() || fallback;
}

export async function downloadOfficialDocument(orderId: string): Promise<GeneratedBusinessReport> {
  const response = await axios.get<Blob>(
    `/api/documents/${encodeURIComponent(orderId)}/file`,
    { responseType: 'blob', timeout: 120_000 },
  );
  return {
    blob: response.data,
    filename: attachmentFilename(response.headers['content-disposition'], `documento-ufficiale-${orderId}.pdf`),
  };
}

export async function fetchCatastoHistory(): Promise<CatastoHistoryRequest[]> {
  const { data } = await axios.get<CatastoHistoryRequest[]>('/api/catasto/requests');
  return data;
}

export async function fetchCatastoRequest(id: string): Promise<CatastoStepResult> {
  const { data } = await axios.get<CatastoStepResult>(`/api/catasto/requests/${encodeURIComponent(id)}`);
  return data;
}

/** SPESA REALE (€0,10): chiamare soltanto dopo conferma esplicita in UI. */
export async function searchCatastoAddress(input: CatastoSearchInput): Promise<CatastoStepResult> {
  const { data } = await axios.post<CatastoStepResult>('/api/catasto/search', input, { timeout: 90_000 });
  return data;
}

/** SPESA REALE (€0,30): chiamare soltanto dopo conferma esplicita in UI. */
export async function searchCatastoImmobili(input: CatastoImmobiliInput): Promise<CatastoStepResult> {
  const { data } = await axios.post<CatastoStepResult>('/api/catasto/immobili', input, { timeout: 90_000 });
  return data;
}

/** SPESA REALE (€1,90): chiamare soltanto dopo conferma esplicita in UI. */
export async function orderCatastoVisura(input: CatastoVisuraInput): Promise<CatastoStepResult> {
  const { data } = await axios.post<CatastoStepResult>('/api/catasto/visura', input, { timeout: 90_000 });
  return data;
}

export async function downloadCatastoDocument(id: string): Promise<GeneratedBusinessReport> {
  const response = await axios.get<Blob>(`/api/catasto/requests/${encodeURIComponent(id)}/documento`, {
    responseType: 'blob',
    timeout: 120_000,
  });
  return {
    blob: response.data,
    filename: attachmentFilename(response.headers['content-disposition'], `visura-catastale-${id}.pdf`),
  };
}

/** Genera un file PDF vero; non usa la stampa del browser. */
export async function generateBusinessReport(request: BusinessReportRequest): Promise<GeneratedBusinessReport> {
  const response = await axios.post<Blob>('/api/report/business', request, {
    responseType: 'blob',
    timeout: 120_000,
  });
  return {
    blob: response.data,
    filename: reportFilename(response.headers['content-disposition'], request.name),
    reportId: response.headers['x-report-id'] || undefined,
  };
}

export interface GeneratedReportSummary {
  id: string;
  title: string;
  lens: 'acquisizione' | 'apertura' | string;
  vat: string | null;
  createdAt: string;
}

/** Snapshot immutabili dei dossier PDF già generati dall'utente. */
export async function fetchGeneratedReports(): Promise<GeneratedReportSummary[]> {
  const { data } = await axios.get<GeneratedReportSummary[]>('/api/reports');
  return data;
}

export async function downloadGeneratedReport(id: string): Promise<GeneratedBusinessReport> {
  const response = await axios.get<Blob>(`/api/reports/${encodeURIComponent(id)}/file`, {
    responseType: 'blob',
    timeout: 120_000,
  });
  return {
    blob: response.data,
    filename: attachmentFilename(response.headers['content-disposition'], `osservio-dossier-${id}.pdf`),
    reportId: id,
  };
}

/** Crea un nuovo snapshot: quello precedente resta nello storico. */
export async function refreshGeneratedReport(id: string): Promise<GeneratedBusinessReport> {
  const response = await axios.post<Blob>(`/api/reports/${encodeURIComponent(id)}/refresh`, null, {
    responseType: 'blob',
    timeout: 120_000,
  });
  return {
    blob: response.data,
    filename: attachmentFilename(response.headers['content-disposition'], `osservio-dossier-aggiornato-${id}.pdf`),
    reportId: response.headers['x-report-id'] || undefined,
  };
}

export async function deleteGeneratedReport(id: string): Promise<void> {
  await axios.delete(`/api/reports/${encodeURIComponent(id)}`);
}

/** Presenza social di un competitor (facebook | instagram | tiktok). */
export interface SocialProfileRow {
  id: string;
  platform: 'facebook' | 'instagram' | 'tiktok';
  username: string | null;
  url: string | null;
  followers: number | null;
  likes: number | null;
  postsCount: number | null;
  avgLikes: number | null;
  avgComments: number | null;
  postsPerWeek: number | null;
  fetchedAt: string;
}

export async function fetchSocial(pageId: string): Promise<SocialProfileRow[]> {
  const { data } = await axios.get<SocialProfileRow[]>(
    `/api/pages/${encodeURIComponent(pageId)}/social`,
  );
  return data;
}

/** Lancia l'arricchimento social via Apify (on-demand, 1-4 minuti). */
export async function enrichSocial(pageId: string, force = false): Promise<SocialProfileRow[]> {
  const { data } = await axios.post<SocialProfileRow[]>(
    `/api/pages/${encodeURIComponent(pageId)}/social`,
    null,
    { params: { force: force || undefined }, timeout: 360_000 },
  );
  return data;
}

/** Post organico di un competitor con metriche pubbliche anonime. */
export interface CompetitorPost {
  id: string;
  platform: 'facebook' | 'instagram';
  url: string | null;
  text: string | null;
  publishedAt: string | null;
  likes: number | null;
  commentsCount: number | null;
  shares: number | null;
  commentSample: string[] | null;
}

export async function fetchPosts(pageId: string): Promise<CompetitorPost[]> {
  const { data } = await axios.get<CompetitorPost[]>(
    `/api/pages/${encodeURIComponent(pageId)}/posts`,
  );
  return data;
}

/** Raccoglie i post organici recenti (Apify on-demand, 1-3 min). */
export async function collectPosts(pageId: string, force = false): Promise<CompetitorPost[]> {
  const { data } = await axios.post<CompetitorPost[]>(
    `/api/pages/${encodeURIComponent(pageId)}/posts`,
    null,
    { params: { force: force || undefined }, timeout: 300_000 },
  );
  return data;
}

/** Snapshot del sito di un competitor (crawl on-demand). */
export interface SiteSnapshot {
  id: string;
  url: string;
  httpStatus: number | null;
  title: string | null;
  metaDescription: string | null;
  pricing: string | null;
  features: string | null;
  changelog: string | null;
  careers: string | null;
  integrations: string | null;
  socialLinks: string | null;
  emails: string | null;
  phones: string | null;
  techPixels: string | null;
  /** Optional so mocks and cached payloads created before section 26 keep working. */
  vatNumber?: string | null;
  vatValid?: boolean | null;
  legalName?: string | null;
  legalAddress?: string | null;
  fetchedAt: string;
}

export interface AutoSiteResult {
  status: 'ok' | 'no_website' | 'not_found' | 'error';
  snapshot: SiteSnapshot | null;
  message: string | null;
}

export async function fetchSite(pageId: string): Promise<SiteSnapshot | null> {
  const { data } = await axios.get<SiteSnapshot | null>(
    `/api/pages/${encodeURIComponent(pageId)}/site`,
  );
  return data;
}

/** Arricchisce il profilo se necessario e recupera il sito, rispettando la cache backend. */
export async function autoCrawlSite(pageId: string): Promise<AutoSiteResult> {
  const { data } = await axios.post<AutoSiteResult>(
    `/api/pages/${encodeURIComponent(pageId)}/site/auto`,
    null,
    { timeout: 120_000 },
  );
  return data;
}

/** Crawla il sito del competitor (homepage + pagine chiave, ~10-30s). */
export async function crawlSite(pageId: string, force = false): Promise<SiteSnapshot> {
  const { data } = await axios.post<SiteSnapshot>(
    `/api/pages/${encodeURIComponent(pageId)}/site`,
    null,
    { params: { force: force || undefined }, timeout: 90_000 },
  );
  return data;
}

/** Crawla on-demand un URL locale non ancora associato a una pagina Meta. */
export async function crawlSiteUrl(url: string, force = false): Promise<SiteSnapshot> {
  const { data } = await axios.post<SiteSnapshot>(
    '/api/site/crawl',
    null,
    { params: { url, force: force || undefined }, timeout: 90_000 },
  );
  return data;
}

export type SiteChangeKind = 'added' | 'removed' | 'changed';

/** Differenza leggibile fra gli ultimi due snapshot del sito. */
export interface SiteChange {
  field: string;
  label: string;
  kind: SiteChangeKind;
  before: string | null;
  after: string | null;
  /** true per segnali deterministici come titolo e pixel; false per sintesi AI. */
  highConfidence: boolean;
}

export interface SiteDiff {
  pageId: string | null;
  url: string | null;
  snapshotCount: number;
  hasComparison: boolean;
  contentChanged: boolean;
  previousAt: string | null;
  currentAt: string | null;
  changes: SiteChange[];
}

export async function fetchSiteChanges(pageId: string): Promise<SiteDiff> {
  const { data } = await axios.get<SiteDiff>(
    `/api/pages/${encodeURIComponent(pageId)}/site/changes`,
  );
  return data;
}

/** Posizione aperta pubblicata su un ATS aziendale. */
export interface JobPosting {
  title: string | null;
  location: string | null;
  department: string | null;
  url: string | null;
  provider: 'greenhouse' | 'lever' | 'ashby' | string;
}

export interface JobsResult {
  slug: string;
  count: number;
  postings: JobPosting[];
}

export async function fetchJobs(slug: string): Promise<JobsResult> {
  const { data } = await axios.get<JobsResult>('/api/jobs', {
    params: { slug: slug.trim() },
    timeout: 45_000,
  });
  return data;
}

/** Immagine della creative (cattura lazy server-side; 404 se assente). */
export function creativeUrl(adId: string): string {
  return `/api/ads/${encodeURIComponent(adId)}/creative`;
}

/** Genera (o rilegge dalla cache) l'analisi AI dei copy del competitor. */
export async function analyzePage(pageId: string, force = false): Promise<PageView> {
  const { data } = await axios.post<PageView>(
    `/api/pages/${encodeURIComponent(pageId)}/analyze`,
    null,
    { params: { force: force || undefined } },
  );
  return data;
}

// ── Trend radar ──────────────────────────────────────────

export interface TrendTopic {
  id: string;
  query: string;
  /** CSV delle fonti attive */
  sources: string;
  active: boolean;
  lastRunAt: string | null;
  createdAt: string;
}

export interface SourceBreakdown {
  source: string;
  count: number;
  authors: number;
  lastPublished: string | null;
}

export interface TrendContent {
  id: string;
  source: string;
  authorName: string | null;
  contentType: string | null;
  title: string | null;
  text: string | null;
  language: string | null;
  country: string | null;
  publishedAt: string | null;
  sourceUrl: string | null;
  mediaUrl: string | null;
}

export interface SourceRun {
  id: string;
  source: string;
  query: string | null;
  startedAt: string;
  status: 'RUNNING' | 'OK' | 'PARTIAL' | 'ERROR';
  itemsReceived: number;
  itemsInserted: number;
  errorMessage: string | null;
}

export type TrendStage = 'ACCELERATING' | 'RISING' | 'STEADY' | 'COOLING' | 'FLAT';

export interface TopicActivity {
  topicId: string;
  query: string;
  active: boolean;
  items7: number;
  itemsPrev: number;
  sources7: number;
  authors7: number;
  /** contenuti/giorno negli ultimi 7gg */
  velocity: number;
  /** Δ contenuti/settimana vs settimana prima (derivata seconda) */
  acceleration: number;
  stage: TrendStage;
}

export interface TopicTheme {
  id: number;
  size: number;
  label: string;
  sampleUrls: string[];
  sources: string[];
  latestAt: string | null;
  last7d: number;
}

export interface ThemesResult {
  topicId: string;
  itemsAnalyzed: number;
  embeddedNow: number;
  themeCount: number;
  themes: TopicTheme[];
}

export interface DuplicateGroup {
  size: number;
  sourceCount: number;
  sources: string[];
  label: string;
  sampleUrls: string[];
  latestAt: string | null;
}

export interface DedupResult {
  topicId: string;
  rawCount: number;
  uniqueCount: number;
  duplicateItems: number;
  embeddedNow: number;
  crossSourceStories: DuplicateGroup[];
  duplicateGroups: DuplicateGroup[];
}

export interface ForecastPoint {
  day: string;
  count: number;
}

export interface ForecastResult {
  topicId: string;
  observedDays: number;
  totalItems: number;
  currentDailyAvg: number;
  trendPerDay: number;
  projectedNext7d: number;
  direction: string;
  confidence: number;
  series: ForecastPoint[];
}

export interface HashtagTrend {
  tag: string;
  count7: number;
  countPrev: number;
  authors: number;
  sources: number;
}

export async function fetchLeaderboard(): Promise<TopicActivity[]> {
  const { data } = await axios.get<TopicActivity[]>('/api/trends/leaderboard');
  return data;
}

export async function fetchHashtags(limit = 30): Promise<HashtagTrend[]> {
  const { data } = await axios.get<HashtagTrend[]>('/api/trends/hashtags', {
    params: { limit },
  });
  return data;
}

export async function fetchTrendSources(): Promise<string[]> {
  const { data } = await axios.get<string[]>('/api/trends/sources');
  return data;
}

export async function fetchTopics(): Promise<TrendTopic[]> {
  const { data } = await axios.get<TrendTopic[]>('/api/trends/topics');
  return data;
}

export async function createTopic(query: string, sources?: string): Promise<TrendTopic> {
  const { data } = await axios.post<TrendTopic>('/api/trends/topics', { query, sources });
  return data;
}

export async function runTopic(id: string): Promise<SourceRun[]> {
  const { data } = await axios.post<SourceRun[]>(
    `/api/trends/topics/${encodeURIComponent(id)}/run`,
  );
  return data;
}

export async function setTopicActive(id: string, value: boolean): Promise<TrendTopic> {
  const { data } = await axios.patch<TrendTopic>(
    `/api/trends/topics/${encodeURIComponent(id)}/active`,
    null,
    { params: { value } },
  );
  return data;
}

export async function deleteTopic(id: string): Promise<void> {
  await axios.delete(`/api/trends/topics/${encodeURIComponent(id)}`);
}

export async function topicBreakdown(id: string): Promise<SourceBreakdown[]> {
  const { data } = await axios.get<SourceBreakdown[]>(
    `/api/trends/topics/${encodeURIComponent(id)}/breakdown`,
  );
  return data;
}

export async function topicContent(id: string, page = 0): Promise<PageResponse<TrendContent>> {
  const { data } = await axios.get<PageResponse<TrendContent>>(
    `/api/trends/topics/${encodeURIComponent(id)}/content`,
    { params: { page, size: 20 } },
  );
  return data;
}

/** Estrae i sotto-temi semantici del topic. Può creare embedding al primo uso. */
export async function fetchTopicThemes(id: string, maxItems = 150): Promise<ThemesResult> {
  const { data } = await axios.get<ThemesResult>(
    `/api/topics/${encodeURIComponent(id)}/themes`,
    { params: { maxItems }, timeout: 90_000 },
  );
  return data;
}

/** Stima le storie uniche e individua quelle presenti su più fonti. */
export async function fetchTopicDedup(id: string, maxItems = 200): Promise<DedupResult> {
  const { data } = await axios.get<DedupResult>(
    `/api/topics/${encodeURIComponent(id)}/dedup`,
    { params: { maxItems }, timeout: 90_000 },
  );
  return data;
}

/** Proiezione statistica, non predizione certa, dei prossimi sette giorni. */
export async function fetchTopicForecast(id: string, days = 28): Promise<ForecastResult> {
  const { data } = await axios.get<ForecastResult>(
    `/api/topics/${encodeURIComponent(id)}/forecast`,
    { params: { days } },
  );
  return data;
}

export type InvestSource = 'binance' | 'frankfurter';
export type InvestAlertDirection = 'above' | 'below';

export interface InvestAlert {
  id: string;
  instrumentId: string;
  direction: InvestAlertDirection;
  threshold: number;
  active: boolean;
  triggeredAt: string | null;
  createdAt: string;
}

export interface InvestOverviewItem {
  id: string;
  symbol: string;
  source: InvestSource | string;
  label: string;
  active: boolean;
  lastClose: number | null;
  lastDay: string | null;
  change24hPct: number | null;
  change7dPct: number | null;
  triggeredAlerts: InvestAlert[];
}

export interface InvestCandle {
  id: string;
  instrumentId: string;
  day: string;
  open: number | null;
  high: number | null;
  low: number | null;
  close: number;
  volume: number | null;
}

export interface CreateInvestInstrument {
  symbol: string;
  source: InvestSource;
  label: string;
}

export interface CreatedInvestInstrument extends CreateInvestInstrument {
  id: string;
  active: boolean;
  createdAt: string;
}

export async function fetchInvestOverview(): Promise<InvestOverviewItem[]> {
  const { data } = await axios.get<InvestOverviewItem[]>('/api/invest/overview');
  return data;
}

export async function fetchInvestCandles(id: string, days: 30 | 90 | 365): Promise<InvestCandle[]> {
  const { data } = await axios.get<InvestCandle[]>(`/api/invest/instruments/${encodeURIComponent(id)}/candles`, { params: { days } });
  return data;
}

export async function collectInvestPrices(days = 120): Promise<Record<string, number>> {
  const { data } = await axios.post<Record<string, number>>('/api/invest/collect', null, { params: { days } });
  return data;
}

export async function createInvestInstrument(input: CreateInvestInstrument): Promise<CreatedInvestInstrument> {
  const { data } = await axios.post<CreatedInvestInstrument>('/api/invest/instruments', input);
  return data;
}

export async function deleteInvestInstrument(id: string): Promise<void> {
  await axios.delete(`/api/invest/instruments/${encodeURIComponent(id)}`);
}

export async function createInvestAlert(id: string, direction: InvestAlertDirection, threshold: number): Promise<InvestAlert> {
  const { data } = await axios.post<InvestAlert>(`/api/invest/instruments/${encodeURIComponent(id)}/alerts`, { direction, threshold });
  return data;
}

export async function deleteInvestAlert(id: string): Promise<void> {
  await axios.delete(`/api/invest/alerts/${encodeURIComponent(id)}`);
}

export interface InvestPositionView {
  instrumentId: string;
  symbol: string;
  label: string;
  qty: number;
  avgPrice: number;
  lastClose: number | null;
  plPct: number | null;
  strategia: string | null;
  /** Numero di acquisti ancora aperti. Presente sui backend dalla V107. */
  rungs?: number | null;
}

export interface InvestPortfolioView {
  id: string;
  name: string;
  mode: 'paper' | string;
  cash: number;
  equityNow: number;
  initialCash: number;
  totalReturnPct: number | null;
  positions: InvestPositionView[];
}

export interface InvestTradeOrder {
  id: string;
  portfolioId: string;
  instrumentId: string;
  strategyId: string | null;
  side: 'buy' | 'sell' | string;
  qty: number;
  price: number;
  fees: number;
  mode: 'paper' | string;
  reason: string;
  executedAt: string;
}

export interface InvestBot {
  id: string;
  name: string;
  titolo: string;
  tipo: string;
  timeframe: '1d' | '1h' | string;
  direction: 'long' | 'short' | 'both' | string;
  symbols: string[] | null;
  active: boolean;
  capitalPct: number | null;
  ordini: number;
  valoreAperto: number;
  pnl: number;
  pnlPct: number | null;
  params: string;
  ultimaMossa: string | null;
}

export interface InvestQuote {
  instrumentId: string;
  symbol: string;
  source: InvestSource | string;
  price: number;
  asOf: string;
}

export interface InvestQuotesResponse {
  quotes: InvestQuote[];
  generatedAt: string;
}

export type InvestBotLevelKind = 'compra' | 'vendi' | 'media' | 'stop' | 'filtro' | string;

export interface InvestBotLevel {
  kind: InvestBotLevelKind;
  label: string;
  price: number;
}

export interface InvestBotMove {
  day: string;
  side: 'buy' | 'sell' | 'short' | 'cover' | string;
  price: number;
  qty?: number;
  reason: string;
}

export interface InvestBotChart {
  livelli: InvestBotLevel[];
  mosse: InvestBotMove[];
}

export interface InvestBotPreview extends InvestBotChart {
  returnPct: number;
  buyHoldReturnPct: number;
  trades: number;
  nota: string;
}

export interface InvestEquityPoint {
  id?: string;
  portfolioId?: string;
  day: string;
  equity: number;
}

export interface InvestStrategy {
  id: string;
  name: string;
  params: string;
  active: boolean;
  capitalPct: number | null;
  version: number;
  createdAt: string;
}

export interface InvestStrategyPnl {
  strategyId: string;
  name: string;
  active: boolean;
  capitalPct: number | null;
  ordini: number;
  investito: number;
  incassato: number;
  valoreAperto: number;
  pnl: number;
  pnlPct: number | null;
}

export interface InvestStrategyCatalogItem {
  type: string;
  nome: string;
  cosaConsidera: string;
  quandoCompra: string;
  quandoVende: string;
  filosofia: string;
  parametri: Record<string, string>;
}

export interface InvestRunResult {
  actions: string[];
  equity: number;
  ordersToday: number;
}

export interface InvestBacktestTrade {
  day: string;
  side: 'buy' | 'sell' | string;
  price: number;
  reason: string;
}

export interface InvestBacktestEquityPoint {
  day: string;
  equity: number;
}

export interface InvestScorePoint {
  day: string;
  noi: number;
  mercato: number;
}

export interface InvestScoreboard {
  noiReturnPct: number;
  mercatoReturnPct: number;
  deltaPct: number;
  serie: InvestScorePoint[];
  stato: 'in_attesa' | 'avanti' | 'indietro';
  mosse: number;
  cassa: number;
  valoreConto: number;
}

export interface InvestBacktestResult {
  symbol: string;
  days: number;
  trades: number;
  returnPct: number;
  buyHoldReturnPct: number;
  maxDrawdownPct: number;
  winRatePct: number | null;
  log: InvestBacktestTrade[];
  equitySeries: InvestBacktestEquityPoint[];
}

export type InvestAiTimeframe = 'daily' | 'intraday';
export type InvestAiHistoryTimeframe = InvestAiTimeframe | 'tournament';
export type InvestAiTrend = 'rialzista' | 'ribassista' | 'laterale' | string;

export interface InvestAiInstrumentReading {
  symbol: string;
  tendenza: InvestAiTrend;
  lettura: string;
  attenzione: string | null;
}

export interface InvestAiContent {
  sintesi: string;
  strumenti: InvestAiInstrumentReading[];
  rischi: string[];
  ipotesi_strategie: Array<InvestAiHypothesis | string>;
}

export interface InvestAiHypothesis {
  nome: string;
  params: Record<string, unknown> | null;
  motivazione: string;
}

export interface InvestAiAnalysis {
  generatedAt: string;
  model: string;
  timeframe: InvestAiTimeframe;
  analisi: InvestAiContent;
}

export type InvestJobName = 'allenamento' | 'torneo' | 'analisi';
export type InvestJobStatus = 'in_corso' | 'pronto' | 'errore';

export interface InvestJob {
  nome: InvestJobName | string;
  stato: InvestJobStatus;
  iniziato: string | null;
  finito: string | null;
  messaggio: string | null;
  risultato: string | null;
}

export interface InvestTournamentCell {
  symbol: string;
  inSampleReturnPct: number | null;
  inSampleBhPct: number | null;
  oosReturnPct: number | null;
  oosBhPct: number | null;
  oosMaxDdPct: number | null;
  oosTrades: number | null;
  oosWinRatePct: number | null;
  oosSharpe: number | null;
  wfBeatBh: number | null;
  wfSegments: number | null;
  mcPctile: number | null;
}

export interface InvestTournamentCandidateScore {
  name: string;
  params: string;
  strumentiBattutiBh: number;
  strumentiPositivi: number;
  mediaExcessOosPct: number | null;
  peggioreMaxDdPct: number | null;
  mediaSharpeOos: number | null;
  celle: InvestTournamentCell[];
}

export interface InvestTournamentRun {
  runId: string;
  createdAt: string;
  source: 'grid' | 'ai' | 'manual' | string;
  timeframe: '1d' | '1h' | string;
  days: number;
  splitPct: number;
  candidates: number;
  strumenti: number;
  classifica: InvestTournamentCandidateScore[];
}

export type InvestTournamentFrame = '1d' | '1h' | '15m';

export type InvestTournamentSemaforo = 'verde' | 'giallo' | 'grigio' | 'dati_insufficienti' | string;

export interface InvestTournamentVerdict {
  semaforo: InvestTournamentSemaforo;
  strumentiGiudicati: number;
  battutiBh: number;
  positivi: number;
  mediaExcessOosPct: number | null;
  mediaSharpeOos: number | null;
}

export interface InvestTournamentMatrixRow {
  name: string;
  params: string;
  perFrame: Partial<Record<InvestTournamentFrame, InvestTournamentVerdict>>;
}

export interface InvestTournamentTotal {
  batchId: string;
  createdAt: string;
  source: 'grid' | 'ai' | 'manual' | 'auto' | string;
  runPerFrame: Partial<Record<InvestTournamentFrame, string>>;
  matrice: InvestTournamentMatrixRow[];
  dettagli: InvestTournamentRun[];
}

export type InvestTournamentRunHead = Omit<InvestTournamentRun, 'classifica'>;

export interface InvestTournamentCandidateInput {
  name: string;
  params: Record<string, unknown>;
}

export interface InvestTournamentRequest {
  candidates?: InvestTournamentCandidateInput[];
  days?: number;
  splitPct?: number;
  timeframe?: '1d' | '1h';
}

export interface InvestTournamentReviewItem {
  nome: string;
  titolo: string;
  giaInSquadra: boolean;
  perche: string;
  params?: Record<string, unknown> | null;
  frame?: InvestTournamentFrame;
}

export interface InvestTournamentReviewHypothesis {
  nome: string;
  titolo: string;
  giaInSquadra: boolean;
  params: Record<string, unknown>;
  motivazione: string;
  frame?: InvestTournamentFrame;
}

export interface InvestTournamentReview {
  generatedAt: string;
  model: string;
  timeframe: 'tournament';
  analisi: {
    runId?: string;
    timeframe?: '1d' | '1h' | string;
    batchId?: string;
    totale?: boolean;
    sintesi: string;
    promuovere: InvestTournamentReviewItem[];
    scartare: InvestTournamentReviewItem[];
    prossime_ipotesi: InvestTournamentReviewHypothesis[];
  };
}

export type InvestAiHistoricalAnalysis = InvestAiAnalysis | InvestTournamentReview;

export interface InvestRebalanceVariant {
  name: string;
  weights: Record<string, number>;
  bandPct: number;
  periodDays: number;
}

export interface InvestRebalanceEquityPoint {
  day: string;
  equity: number;
}

export interface InvestRebalanceResult extends InvestRebalanceVariant {
  from: string;
  to: string;
  ribilanciamenti: number;
  feesTotali: number;
  returnPct: number;
  bhReturnPct: number;
  maxDdPct: number;
  bhMaxDdPct: number;
  oosReturnPct: number | null;
  oosBhPct: number | null;
  oosMaxDdPct: number | null;
  equitySeries: InvestRebalanceEquityPoint[];
}

export interface InvestRebalanceRequest {
  variants?: InvestRebalanceVariant[];
  days?: number;
  splitPct?: number;
}

export interface RunInvestBacktestInput {
  strategyId: string;
  instrumentId: string;
  days: number;
}

export async function fetchInvestPortfolio(): Promise<InvestPortfolioView> {
  const { data } = await axios.get<InvestPortfolioView>('/api/invest/portfolio');
  return data;
}

export async function fetchInvestOrders(page = 0, size = 50): Promise<PageResponse<InvestTradeOrder>> {
  const { data } = await axios.get<PageResponse<InvestTradeOrder>>('/api/invest/orders', { params: { page, size } });
  return data;
}

export async function fetchInvestEquity(days = 180): Promise<InvestEquityPoint[]> {
  const { data } = await axios.get<InvestEquityPoint[]>('/api/invest/equity', { params: { days } });
  return data;
}

export async function fetchInvestScoreboard(days = 365): Promise<InvestScoreboard> {
  const { data } = await axios.get<InvestScoreboard>('/api/invest/scoreboard', { params: { days } });
  return data;
}

export async function fetchInvestStrategies(): Promise<InvestStrategy[]> {
  const { data } = await axios.get<InvestStrategy[]>('/api/invest/strategies');
  return data;
}

export async function fetchInvestBots(): Promise<InvestBot[]> {
  const { data } = await axios.get<InvestBot[]>('/api/invest/bots');
  return data;
}

export async function fetchInvestQuotes(): Promise<InvestQuotesResponse> {
  const { data } = await axios.get<InvestQuotesResponse>('/api/invest/quotes');
  return data;
}

export async function fetchInvestBotChart(botId: string, instrumentId: string, days = 90): Promise<InvestBotChart> {
  const { data } = await axios.get<InvestBotChart>(`/api/invest/bots/${encodeURIComponent(botId)}/chart`, {
    params: { instrumentId, days },
  });
  return data;
}

export async function previewInvestBot(
  instrumentId: string,
  params: string | Record<string, unknown>,
  days = 90,
): Promise<InvestBotPreview> {
  let parsed: Record<string, unknown>;
  if (typeof params === 'string') {
    try {
      parsed = JSON.parse(params) as Record<string, unknown>;
    } catch {
      parsed = {};
    }
  } else {
    parsed = params;
  }
  const { data } = await axios.post<InvestBotPreview>('/api/invest/bots/preview', {
    instrumentId,
    params: parsed,
    days,
  });
  return data;
}

export async function fetchInvestStrategiesPnl(): Promise<InvestStrategyPnl[]> {
  const { data } = await axios.get<InvestStrategyPnl[]>('/api/invest/strategies/pnl');
  return data;
}

export async function fetchInvestStrategiesCatalog(): Promise<InvestStrategyCatalogItem[]> {
  const { data } = await axios.get<InvestStrategyCatalogItem[]>('/api/invest/strategies/catalog');
  return data;
}

export async function setInvestStrategyActive(id: string, active: boolean): Promise<InvestStrategy> {
  const { data } = await axios.patch<InvestStrategy>(`/api/invest/strategies/${encodeURIComponent(id)}`, null, { params: { active } });
  return data;
}

export async function setInvestStrategyCapital(id: string, pct: number): Promise<InvestStrategy> {
  const { data } = await axios.patch<InvestStrategy>(`/api/invest/strategies/${encodeURIComponent(id)}/capital`, null, { params: { pct } });
  return data;
}

export async function deleteInvestStrategy(id: string): Promise<void> {
  await axios.delete(`/api/invest/strategies/${encodeURIComponent(id)}`);
}

export async function runInvestCycle(timeframe: '1d' | '1h' = '1d'): Promise<InvestRunResult> {
  const { data } = await axios.post<InvestRunResult>('/api/invest/run', null, { params: { timeframe } });
  return data;
}

export async function downloadInvestReport(): Promise<GeneratedBusinessReport> {
  const response = await axios.get<Blob>('/api/invest/report.pdf', {
    responseType: 'blob',
    timeout: 120_000,
  });
  return {
    blob: response.data,
    filename: attachmentFilename(response.headers['content-disposition'], 'investlab-report.pdf'),
  };
}

export async function fetchInvestAiAnalysis(timeframe: InvestAiTimeframe): Promise<InvestAiAnalysis | null> {
  try {
    const { data } = await axios.get<InvestAiAnalysis>('/api/invest/ai/analysis', { params: { timeframe } });
    return data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 404) return null;
    throw error;
  }
}

export async function startInvestAiAnalysis(timeframe: InvestAiTimeframe): Promise<InvestJob> {
  const { data } = await axios.post<InvestJob>('/api/invest/ai/analysis/start', null, { params: { timeframe } });
  return data;
}

export async function fetchInvestAiAnalysisHistory(timeframe: InvestAiHistoryTimeframe, limit = 10): Promise<InvestAiHistoricalAnalysis[]> {
  const { data } = await axios.get<InvestAiHistoricalAnalysis[]>('/api/invest/ai/analysis/history', { params: { timeframe, limit } });
  return data;
}

export async function fetchInvestTournamentRuns(): Promise<InvestTournamentRunHead[]> {
  const { data } = await axios.get<InvestTournamentRunHead[]>('/api/invest/tournament/runs');
  return data;
}

export async function fetchInvestTournament(runId: string): Promise<InvestTournamentRun> {
  const { data } = await axios.get<InvestTournamentRun>(`/api/invest/tournament/${encodeURIComponent(runId)}`);
  return data;
}

export async function runInvestTournament(input?: InvestTournamentRequest): Promise<InvestTournamentRun> {
  // sempre JSON esplicito: con body null Safari inviava x-www-form-urlencoded → 415
  const { data } = await axios.post<InvestTournamentRun>('/api/invest/tournament', input ?? {}, { timeout: 120_000 });
  return data;
}

export async function runInvestTournamentFromAi(): Promise<InvestTournamentRun> {
  const { data } = await axios.post<InvestTournamentRun>('/api/invest/tournament/from-ai', null, { timeout: 120_000 });
  return data;
}

export async function startInvestTournamentTotal(input?: Omit<InvestTournamentRequest, 'timeframe'>): Promise<InvestJob> {
  const { data } = await axios.post<InvestJob>('/api/invest/tournament/total/start', input ?? {});
  return data;
}

export async function fetchInvestTournamentTotal(batchId: string): Promise<InvestTournamentTotal> {
  const { data } = await axios.get<InvestTournamentTotal>(`/api/invest/tournament/total/${encodeURIComponent(batchId)}`);
  return data;
}

export async function fetchInvestTournamentReview(): Promise<InvestTournamentReview | null> {
  try {
    const { data } = await axios.get<InvestTournamentReview>('/api/invest/ai/tournament-review');
    return data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 404) return null;
    throw error;
  }
}

export async function generateInvestTournamentReview(runId: string): Promise<InvestTournamentReview> {
  const { data } = await axios.post<InvestTournamentReview>('/api/invest/ai/tournament-review', null, {
    params: { runId },
    timeout: 210_000,
  });
  return data;
}

export async function startInvestTraining(): Promise<InvestJob> {
  const { data } = await axios.post<InvestJob>('/api/invest/training/start');
  return data;
}

export async function fetchInvestJob(name: InvestJobName): Promise<InvestJob | null> {
  try {
    const { data } = await axios.get<InvestJob>(`/api/invest/jobs/${encodeURIComponent(name)}`);
    return data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 404) return null;
    throw error;
  }
}

export async function createInvestStrategy(name: string, params: string): Promise<InvestStrategy> {
  const { data } = await axios.post<InvestStrategy>('/api/invest/strategies', { name, params });
  return data;
}

export async function promoteInvestTournamentCandidate(name: string, params: string): Promise<InvestStrategy> {
  return createInvestStrategy(name, params);
}

export async function runInvestRebalance(input?: InvestRebalanceRequest): Promise<InvestRebalanceResult[]> {
  const { data } = await axios.post<InvestRebalanceResult[]>('/api/invest/rebalance/backtest', input ?? {}, { timeout: 120_000 });
  return data;
}

export function apiStatus(error: unknown): number | null {
  return axios.isAxiosError(error) ? error.response?.status ?? null : null;
}

export async function runInvestBacktest(input: RunInvestBacktestInput): Promise<InvestBacktestResult> {
  const { data } = await axios.post<InvestBacktestResult>('/api/invest/backtest', input);
  return data;
}

/** URL pubblica dell'annuncio nell'Ad Library (non richiede token, mostra i media). */
export function adLibraryUrl(metaAdArchiveId: string): string {
  return `https://www.facebook.com/ads/library/?id=${encodeURIComponent(metaAdArchiveId)}`;
}

/** Messaggio leggibile da un errore axios/JS qualsiasi. */
export function errorMessage(err: unknown): string {
  if (axios.isAxiosError(err)) {
    // Prima il messaggio del backend (es. "Token Meta scaduto"): un 502 CON
    // body spiega il problema vero, non è un backend giù
    const detail =
      (err.response?.data as { message?: string } | undefined)?.message;
    if (detail) return detail;
    if (err.code === 'ERR_NETWORK' || err.response?.status === 502) {
      return 'Backend non raggiungibile. Verifica che lo stack Docker sia attivo.';
    }
    if (err.response?.status) return `Errore del server (HTTP ${err.response.status}).`;
  }
  return err instanceof Error ? err.message : 'Errore sconosciuto.';
}
